import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import { ThemeProvider } from "./context/ThemeContext";
import { BookingProvider } from "./context/BookingContext";
import { GooglePlacesProvider } from "./context/GooglePlacesContext";
import Header from "./components/layout/Header";
import ContactForm from "./components/forms/ContactForm";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { initializeSession } from "./utils/cookies";
import { analytics } from "./utils/analytics";
import { initGA4 } from "./utils/gtag";
import { databaseService } from "./services/databaseService";
import { userJourneyService } from "./services/userJourneyService";
import { preloadCriticalComponents } from "./utils/dynamicImports";
import { PerformanceProfiler } from "./utils/performanceProfiler";
import {
  usePerformanceMonitoring,
  trackBundleSize,
} from "./utils/performanceUtils";
import "./services/utmPromoService"; // Initialize UTM promo service
import "./services/campaignTokenService"; // Initialize campaign token service (?t= URL param)
import Spinner from "./components/common/Spinner";

// Lazy load heavy components
const DetailsForm = lazy(() => import("./components/forms/DetailsForm"));
const FileUploadPage = lazy(
  () => import("./components/booking/FileUploadPage")
);
const BookingCalendar = lazy(
  () => import("./components/booking/BookingCalendar")
);
const ConfirmationPage = lazy(
  () => import("./components/booking/ConfirmationPage")
);
const PriceEstimationPage = lazy(
  () => import("./components/booking/PriceEstimationPage")
);
const SettingsPage = lazy(() => import("./components/admin/SettingsPage"));
const AnalyticsDashboard = lazy(
  () => import("./components/admin/AnalyticsDashboard")
);
const LeadsDashboard = lazy(
  () => import("./components/dashboard/LeadsDashboard")
);
const SimproSettingsPanel = lazy(
  () => import("./components/booking/SimproSettingsPanel")
);
const RescheduleBookingPage = lazy(
  () => import("./components/booking/RescheduleBookingPage")
);
const AdminTroubleshootSettings = lazy(
  () => import("./components/admin/AdminTroubleshootSettings")
);
const SessionLogsPage = lazy(() => import("./components/admin/SessionLogsPage"));
const BookingManagementPage = lazy(
  () => import("./components/booking/BookingManagementPage")
);
const TroubleshootingBookingBridge = lazy(
  () => import("./components/booking/TroubleshootingBookingBridge")
);
// Promotional banner component - currently not used but available for future use
// const PromotionalBannerComponent = lazy(
//   () => import("./components/common/PromotionalBanner")
// );
const MobileLeadsDashboard = lazy(
  () => import("./pages/MobileLeadsDashboard")
);
const PodiumTestPage = lazy(
  () => import("./pages/PodiumTestPage")
);
const AnalyticsTestPage = lazy(
  () => import("./pages/AnalyticsTestPage")
);
const WarrantyConfirmationPage = lazy(
  () => import("./pages/WarrantyConfirmationPage")
);

console.log("Alpha Air Self Service Application - Optimized loading!");

// Optimized QueryClient for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: 1, // Reduce retries for faster failures
      refetchOnWindowFocus: false, // Reduce unnecessary refetches
    },
    mutations: {
      retry: 1,
    },
  },
});

// Modern Error Boundary Component using React 18+ patterns
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; retry: () => void }>;
}

// Keep the class component for Error Boundary as it's still needed for componentDidCatch
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean; error: Error | null }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Filter out browser extension related errors
    if (error.message?.includes('message channel closed') ||
        error.message?.includes('listener indicated an asynchronous response')) {
      console.debug('Filtered extension-related error:', error.message);
      return; // Don't track these errors
    }

    console.error("Application error:", error, errorInfo);
    analytics.trackError("app_error", error.message);
    
    // Send to analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: true,
        custom_parameter_1: 'error_boundary'
      });
    }
  }

  retry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} retry={this.retry} />;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <div className="mb-4">
              <div className="mx-auto h-12 w-12 text-red-500">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We're sorry, but something unexpected happened. You can try again or refresh the page.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={this.retry}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="border border-gray-300 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Refresh Page
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-gray-500">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-auto">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  // Enable performance monitoring
  usePerformanceMonitoring();

  useEffect(() => {
    // Suppress browser extension message channel errors
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes('message channel closed') ||
          event.reason?.message?.includes('listener indicated an asynchronous response')) {
        console.debug('Suppressed extension-related error:', event.reason.message);
        event.preventDefault();
        return;
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        PerformanceProfiler.mark("app-init-start");

        // Initialize session first
        PerformanceProfiler.mark("session-init-start");
        const sessionId = initializeSession();
        PerformanceProfiler.measure("session-init", "session-init-start");
        
        console.log('🔑 Session initialized:', sessionId);

        // Initialize GA4 tracking
        PerformanceProfiler.mark("ga4-init-start");
        initGA4();
        PerformanceProfiler.measure("ga4-init", "ga4-init-start");
        console.log('📊 GA4 initialized with measurement ID: G-NZ7PJRQ25N');

        // Initialize user journey tracking
        PerformanceProfiler.mark("user-journey-start");
        userJourneyService.initializeJourney();
        userJourneyService.recordStep("app_start", {
          timestamp: new Date().toISOString(),
          url: window.location.href,
          referrer: document.referrer || "direct",
        });
        PerformanceProfiler.measure("user-journey-init", "user-journey-start");

        // Track app start (only after session is confirmed initialized)
        PerformanceProfiler.mark("analytics-start");
        if (sessionId && sessionId !== 'undefined') {
          analytics.trackPageView("app_start");
        } else {
          console.warn('📊 App: Skipping initial analytics tracking due to invalid session');
        }
        PerformanceProfiler.measure("analytics-init", "analytics-start");

        // Initialize database tables (gracefully handle failures)
        try {
          PerformanceProfiler.mark("database-init-start");
          await databaseService.initializeTables();
          PerformanceProfiler.measure("database-init", "database-init-start");
        } catch (error) {
          console.warn(
            "Database initialization failed, continuing with localStorage fallback:",
            error
          );
        }

        // Start preloading components after initial load
        PerformanceProfiler.mark("preload-start");
        preloadCriticalComponents();
        PerformanceProfiler.measure("preload-components", "preload-start");

        // Track bundle performance in development
        if (process.env.NODE_ENV === "development") {
          setTimeout(trackBundleSize, 1000);
        }

        PerformanceProfiler.measure("total-app-init", "app-init-start");
      } catch (error) {
        console.error("App initialization error:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        analytics.trackError("app_init_error", errorMessage);
      }
    };

    initializeApp();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <GooglePlacesProvider>
            <BookingProvider>
              <Router>
                <div className="App min-h-screen bg-gray-50 dark:bg-gray-900">
                  <Header />
                  <main>
                    <Suspense
                      fallback={
                        <div className="flex justify-center items-center min-h-[200px]">
                          <Spinner size="lg" />
                        </div>
                      }
                    >
                      <Routes>
                        <Route path="/" element={<ContactForm />} />
                        <Route path="/contact" element={<ContactForm />} />
                        <Route path="/details" element={<DetailsForm />} />
                        <Route path="/files" element={<FileUploadPage />} />
                        <Route path="/upload" element={<FileUploadPage />} />
                        <Route path="/booking" element={<BookingCalendar />} />
                        <Route path="/calendar" element={<BookingCalendar />} />
                        <Route
                          path="/confirmation"
                          element={<ConfirmationPage />}
                        />
                        <Route
                          path="/warranty-confirmation"
                          element={<WarrantyConfirmationPage />}
                        />
                        <Route
                          path="/price-estimation"
                          element={<PriceEstimationPage />}
                        />
                        <Route
                          path="/troubleshoot-booking-bridge"
                          element={<TroubleshootingBookingBridge />}
                        />
                        <Route
                          path="/reschedule/:bookingId"
                          element={<RescheduleBookingPage />}
                        />
                        <Route
                          path="/reschedule"
                          element={<RescheduleBookingPage />}
                        />
                        <Route
                          path="/booking-management"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_SETTINGS_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="booking-management"
                            >
                              <BookingManagementPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_SETTINGS_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="settings"
                            >
                              <SettingsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/logs"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_SETTINGS_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="session-logs"
                            >
                              <SessionLogsPage onClose={() => window.history.back()} />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/simpro-settings"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_SETTINGS_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="simpro-settings"
                            >
                              <SimproSettingsPanel />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/dashboard"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_DASHBOARD_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="dashboard"
                            >
                              <AnalyticsDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/leads"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_DASHBOARD_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="leads"
                            >
                              <LeadsDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/mobile"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_DASHBOARD_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="mobile-leads"
                            >
                              <MobileLeadsDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/podium-test"
                          element={<PodiumTestPage />}
                        />
                        <Route
                          path="/analytics-test"
                          element={<AnalyticsTestPage />}
                        />
                        <Route
                          path="/admin-troubleshoot"
                          element={
                            <ProtectedRoute
                              passwordEnvVar="REACT_APP_SETTINGS_PASSWORD"
                              fallbackPassword="admin123"
                              routeName="admin-troubleshoot"
                            >
                              <AdminTroubleshootSettings />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                    </Suspense>
                  </main>
                </div>
              </Router>
            </BookingProvider>
          </GooglePlacesProvider>
        </ThemeProvider>
      </QueryClientProvider>
      <SpeedInsights />
      <Analytics />
    </ErrorBoundary>
  );
}

export default App;
